import { useEffect, useRef, useState } from "react";

const PLAYBACK_RATE = 1.5;

export default function VideoBackground({ src, autoPlay, setVideoPlay, next, nextSrc = null, road }) {
    const [play, setPlay] = useState(autoPlay);
    const videoRef = useRef(null);
    const preloadVideoRef = useRef(null);
    const lastFrameCanvasRef = useRef(null);
    const [videoStarted, setVideoStarted] = useState(false);
    
    // Handle current video playback
    useEffect(() => {
        setPlay(autoPlay);
        if (autoPlay && videoRef.current) {
            // When a new video is about to play, hide the last frame canvas
            if (lastFrameCanvasRef.current) {
                lastFrameCanvasRef.current.style.display = 'none';
            }
            
            videoRef.current.playbackRate = PLAYBACK_RATE;
            videoRef.current.play().catch(error => {
                console.error("Erreur lors de la lecture automatique :", error);
            });
        } else if (!autoPlay && videoRef.current) {
            videoRef.current.pause();
        }
    }, [autoPlay, src]);

    // Preload the next video
    useEffect(() => {
        if (nextSrc && preloadVideoRef.current) {
            preloadVideoRef.current.src = nextSrc;
            preloadVideoRef.current.load();
        }
    }, [nextSrc]);

    
    // Capture the last frame before the video ends
    const captureLastFrame = () => {
        if (!videoRef.current || !lastFrameCanvasRef.current) return;
        
        const video = videoRef.current;
        const canvas = lastFrameCanvasRef.current;
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Draw the current video frame to the canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Show the canvas with the frozen last frame
        canvas.style.display = 'block';
    };
    
    // Handle video ended event
    const handleVideoEnded = () => {
        // Capture the last frame before proceeding
        captureLastFrame();
        
        // Notify parent components
        setVideoPlay(false);
        next();
    };

    return (
        <div style={{ position: 'fixed', width: '100%', height: '100%', overflow: 'hidden', backgroundColor: 'black' }}>
            {/* Canvas to hold the last frame */}
            <canvas 
                ref={lastFrameCanvasRef}
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    minWidth: '100%',
                    minHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'cover',
                    display: 'none',
                    zIndex: 1 // Above video but below other elements
                }}
            />
            {/* First frame image */}
            {!videoStarted && (
                <img 
                    src={`./assets/first-frame-${road}.jpg`}
                    alt="First Frame"
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        minWidth: '100%',
                        minHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'cover',
                        zIndex: 0
                    }}
                />
            )}

            
            {/* Current video */}
            <video 
                onPlay={() => setVideoStarted(true)}
                onEnded={handleVideoEnded}
                ref={videoRef}
                muted
                playsInline
                autoPlay={play}
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    minWidth: '100%',
                    minHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'cover',
                    zIndex: 0
                }}
                src={src}
            />
            
            {/* Hidden video element for preloading */}
            <video 
                ref={preloadVideoRef}
                muted
                playsInline
                preload="auto"
                style={{ display: 'none' }}
            />
        </div>
    );
}